/**
 * File: SelectProjectToolUI.tsx
 * Description: In-chat project picker shown when the assistant is used without
 *   an active project (e.g. on the all-projects front page). Selecting a
 *   project sets it as the active project and the chat switches to it without
 *   navigating away from the current page.
 * Author: System
 * Created: 2026-06-11
 * Last Modified: 2026-06-11
 */

import { makeAssistantToolUI } from '@assistant-ui/react';
import { FolderOpenIcon } from '@heroicons/react/24/outline';
import type { FC } from 'react';

import { setActiveProjectId } from '../../utils/activeProject';

// Name of the synthetic tool emitted by the chat adapter when no project is
// active. Keep in sync with the adapter in ChatRuntime.tsx.
export const SELECT_PROJECT_TOOL_NAME = 'select_project';

interface SelectProjectOption {
  id: number;
  title: string;
}

interface SelectProjectArgs {
  projects?: SelectProjectOption[];
}

interface ProjectPickerProps {
  projects: SelectProjectOption[];
}

/**
 * Render the clickable list of projects inside an assistant message.
 */
const ProjectPicker: FC<ProjectPickerProps> = ({ projects }) => {
  if (!projects || projects.length === 0) {
    return (
      <div className="rounded-lg border border-base-300 bg-base-200 p-3 text-sm text-base-content/70">
        No projects are available yet. Create a project to start chatting.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {projects.map((project) => (
        <button
          key={project.id}
          type="button"
          onClick={() => setActiveProjectId(project.id)}
          className="flex items-center gap-2 rounded-lg border border-base-300 bg-base-200 p-2 text-left text-sm text-base-content transition-colors hover:bg-base-300"
        >
          <FolderOpenIcon className="h-4 w-4 flex-shrink-0 text-primary" />
          <span className="truncate">{project.title}</span>
        </button>
      ))}
    </div>
  );
};

/**
 * Assistant tool UI that renders the project picker for the select_project
 * synthetic tool call.
 */
export const SelectProjectToolUI = makeAssistantToolUI<SelectProjectArgs, string>({
  toolName: SELECT_PROJECT_TOOL_NAME,
  render: ({ args }) => <ProjectPicker projects={args?.projects ?? []} />,
});
